import data from "../../shared/sharedData";

export default {
  name: "Employer Point-of-Contact Information",
  prefix: "B",
  symbol: "list",
  pageInfo: {
    text: `An employer point of contact is a person employed by the employer whose position authorizes the person to provide information and supporting documentation concerning the <i>Application for Permanent Employment Certification</i> and to communicate with the Department on behalf of the employer.  The employer point of contact should be the individual most familiar with the content of the application and circumstances of the permanent employment offered through this application.<br/><br/><b><u>Note:</u></b> The employer point of contact information in this Section—specifically, the name, telephone number, and email address—<u>must</u> be different from the attorney/agent information listed in Section C, except when an attorney listed in Section C is an employee of the employer.`,
    coloring: "grey",
  },
  sections: [
    {
      name: "Name & Title",
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer Point of Contact profile to populate this section (optional)",
          code: "B",
          prefill: {
            schema: "employerPocs",
            type: "employer-pocs",
            formName: "9089",
            formSection: "B",
          },
        },
        {
          code: "B.1",
          label: "Contact’s Last (family) Name",
          machine_name: "empPocLastName",
          widget: "text",
          tooltip:
            "Enter the last (family) name of the employer’s point of contact.",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Last Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.2",
          label: "First (given) Name",
          machine_name: "empPocFirstName",
          widget: "text",
          tooltip:
            "Enter the first (given) name of the employer’s point of contact.",
          validate: [
            "required",
            {
              minLength: 2,
              message: "First Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.3",
          label: "Middle Name(s) §",
          machine_name: "empPocMiddleName",
          widget: "text",
          tooltip:
            "Enter the middle name(s) of the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
          maxLength: 35,
        },
        {
          code: "B.4",
          label: "Contact's Job Title",
          machine_name: "empPocJobTitle",
          widget: "text",
          tooltip: "Enter the job title of the employer’s point of contact.",
          validate: ["required", "noNAs"],
          maxLength: 60,
        },
      ],
    },
    {
      name: "Address",
      fields: [
        {
          code: "B.5",
          label: "Address 1",
          machine_name: "empPocAddr1",
          widget: "text",
          tooltip:
            "Enter the business street address of the employer’s point of contact. The place of business must be a physical location and not a P.O. Box.",
          validate: [
            "required",
            {
              minLength: 2,
              message: `Address 1 must contain at least 2 characters.`,
            },
            "noNAs",
          ],
          maxLength: 60,
        },
        {
          code: "B.6",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span> §",
          machine_name: "empPocAddr2",
          widget: "text",
          tooltip:
            "If additional space is required for the street address, use this field to complete the street address.  If no additional space is required, enter “N/A.”",
          maxLength: 60,
        },
        {
          code: "B.10",
          label: "Country",
          machine_name: "empPocCountry",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["empPocState", "empPocProvince"],
          tooltip: "Enter the country of the employer’s point of contact.",
        },
        {
          code: "B.7",
          label: "City",
          machine_name: "empPocCity",
          widget: "text",
          tooltip: `Enter the city of the employer’s point of contact.  If the city in B.7 and the country in B.10 are the same,
            the name <u>must</u> be entered in <u>both</u> fields.`,
          disabled: true,
          disableWhenField: "empPocCountry",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 35,
        },
        {
          code: "B.8",
          label: "State",
          machine_name: "empPocState",
          widget: "select",
          selection: data.state,
          state: true,
          maxLength: 35,
          disabled: true,
          disableWhenField: "empPocCountry",
          isVisible: {
            when: "empPocCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "empPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip:
            "Enter the state, district, or territory of the employer’s point of contact.",
        },
        {
          code: "B.9",
          label: "Postal Code",
          machine_name: "empPocPostalCode",
          widget: "text",
          disabled: true,
          disableWhenField: "empPocCountry",
          validate: [
            {
              postalCountry: "empPocCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "empPocCountry",
              },
            },
            {
              isRequired: "empPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          //minLength: 5,
          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer’s point of contact.",
        },
        {
          code: "B.11",
          machine_name: "empPocProvince",
          widget: "text",
          label: "Province §",
          disabled: true,
          disableWhenField: "empPocCountry",
          validate: [
            {
              isRequired: "empPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          maxLength: 25,
          tooltip:
            "Enter the province of the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Contact Information",
      fields: [
        {
          code: "B.12",
          label: "Telephone Number",
          machine_name: "empPocPhone",
          widget: "phone",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and telephone number for the employer’s point of contact.  Include country code, if outside of the United States.",
        },
        {
          code: "B.13",
          label: "Extension §",
          machine_name: "empPocPhoneExt",
          widget: "text",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number for the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
        },
        {
          code: "B.14",
          label: "Business Email Address",
          machine_name: "empPocEmail",
          widget: "text",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
          ],
          tooltip: `Enter the business email address of the employer’s point of contact in the format <u>name@emailaddress.top-level</u> domain.
              The email entered in this field must be the same as the one regularly used by the employer’s point of contact for 
              its business operations and must be capable of sending and receiving electronic communications 
              from the Department with respect to the processing of this application. 
              If the employer’s point of contact does not possess a business email address, please enter “N/A.”`,
          maxLength: 100,
        },
      ],
    },
  ],
};
